import type {
  AddDraftBranchStepRequest,
  AddDraftEventRequest,
  AddDraftStepRequest,
  ConnectDraftStepsRequest,
  DraftNodeEnvelope,
  GetProcessDraftResponse,
  InsertDraftStepBetweenRequest,
  MergeDraftNodesRequest,
  SetDraftStepTerminalRequest
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { basePath } from '../mutations/useProcessDraft/shared';

/**
 * Persistent undo/redo (ADR 0007). Both step the draft one entry along its
 * server-side history and return the FULL draft envelope (not a node envelope)
 * — the whole draft state is replaced, so callers swap it into the cache.
 */
export async function undoDraftApi(processId: string): Promise<GetProcessDraftResponse> {
  const { data } = await apiClient.post<GetProcessDraftResponse>(`${basePath(processId)}/undo`);
  return data;
}

export async function redoDraftApi(processId: string): Promise<GetProcessDraftResponse> {
  const { data } = await apiClient.post<GetProcessDraftResponse>(`${basePath(processId)}/redo`);
  return data;
}

/**
 * Persist-only wrappers around the draft-mutation HTTP endpoints. These are
 * the plain network calls — no cache side effects, no optimistic writes, no
 * reconciliation. Callers that want cache effects either use the mutation
 * hooks (single-op) or `runDraftTransaction` (compound ops). Call these inside
 * a `useDraftGesture` run: on their own they are not in the mutation cache, so
 * Publish's flush and "Saving…" cannot see them.
 *
 * Every draft mutation responds with the uniform `DraftNodeEnvelope` — the
 * touched nodes, each self-describing its cache collection via `nodeKind`.
 * Callers reconcile via `reconcileEnvelope` (or `graph.applyEnvelope`) and
 * pull specific docs out with `expectSingleNode` when needed.
 */

export async function addDraftStepApi(
  processId: string,
  body: AddDraftStepRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(`${basePath(processId)}/steps`, body);
  return data;
}

export async function connectDraftStepsApi(
  processId: string,
  stepId: string,
  body: ConnectDraftStepsRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(
    `${basePath(processId)}/steps/${stepId}/successors`,
    body
  );
  return data;
}

export async function disconnectDraftStepsApi(
  processId: string,
  stepId: string,
  targetStepId: string,
  ruleId?: string
): Promise<DraftNodeEnvelope> {
  const query = ruleId ? `?ruleId=${encodeURIComponent(ruleId)}` : '';
  const { data } = await apiClient.delete<DraftNodeEnvelope>(
    `${basePath(processId)}/steps/${stepId}/successors/${targetStepId}${query}`
  );
  return data;
}

/**
 * Transactionally insert a new task step M between a source (step, assignment
 * rule handle, or the start event) and its current successors. Replaces the
 * editor's old add+PATCH sequences — the backend rewires every target, clears
 * the source's ended marker (or transfers it onto M for a terminal insert) and
 * seeds M's layout position in one transaction. Ids are `stableStepId`s
 * throughout (the draft graph's identity space).
 */
export async function insertDraftStepBetweenApi(
  processId: string,
  body: InsertDraftStepBetweenRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(
    `${basePath(processId)}/steps/insert-between`,
    body
  );
  return data;
}

/**
 * Transactionally add a new task step M wired after a source step ("open
 * parallel branch", optionally appending a decision rule) or before a target
 * node ("merge parallel branch"). M's `stableStepId` is `createdNodeIds[0]`.
 */
export async function addDraftBranchStepApi(
  processId: string,
  body: AddDraftBranchStepRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(
    `${basePath(processId)}/steps/branch`,
    body
  );
  return data;
}

/**
 * Transactionally mark a step's task handle, or some of its decision outputs,
 * as ending or unclear: downstream steps lose the edge, downstream draft
 * assignments are deleted (their ids come back in `deletedNodeIds`).
 * Addressed by `stableStepId`.
 */
export async function setDraftStepTerminalApi(
  processId: string,
  stableStepId: string,
  body: SetDraftStepTerminalRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(
    `${basePath(processId)}/steps/${stableStepId}/terminal`,
    body
  );
  return data;
}

/**
 * Merge one draft node into another. The merged node is folded into the
 * survivor (referrers rewired, predecessors + accumulable content absorbed)
 * and deleted; the survivor's kind is the result kind. The envelope reports
 * the survivor under its (possibly cross-kind) `nodeKind`, every rewired /
 * recomputed neighbor, and the merged node in `deletedNodeIds`.
 */
export async function mergeDraftNodesApi(
  processId: string,
  body: MergeDraftNodesRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(
    `${basePath(processId)}/nodes/merge`,
    body
  );
  return data;
}

export async function addDraftEventApi(
  processId: string,
  body: AddDraftEventRequest
): Promise<DraftNodeEnvelope> {
  const { data } = await apiClient.post<DraftNodeEnvelope>(`${basePath(processId)}/events`, body);
  return data;
}
